import React from 'react';
import { MultiValue } from 'react-select';
import EpdSelectComponent from 'components/form/EpdSelectComponent';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { useDynamicFormsDocumentCollections, useEpdCollectionsLookup } from 'services/api/queries';
import { useUpdateDocumentCollections } from 'services/api/mutations';
import { Option } from 'types/types';
import { DocumentActionState } from 'types/dynamic-forms/DocumentModelStateActions';
import { useDocumentStore } from '../hooks/useDocumentStore';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import VersionHistoryDialog from '../components/dialogs/VersionHistoryDialog';
import VersionComparerDialog from '../components/dialogs/VersionComparerDialog';
import { useDynamicFormsDocumentVersions } from 'services/api/queries';
import { useHasRole } from 'hooks/useRoleCheck';
import { RoleName } from 'constants/constants';
import DocumentCommentLogButton from '../../comment-log/DocumentCommentLogButton';

const EpdMetadataRenderer: React.FC = () => {
  const { metadata, docId, state } = useDocumentStore();
  const collectionsLookup = useEpdCollectionsLookup(metadata.companyId);
  const documentCollections = useDynamicFormsDocumentCollections(docId);
  const updateDocumentCollections = useUpdateDocumentCollections(docId);
  const documentVersions = useDynamicFormsDocumentVersions(docId);
  const isAdmin = useHasRole(RoleName.ADMIN);
  const isLicenseeAdmin = useHasRole(RoleName.LICENSEE_ADMIN);
  const collectionOptions = collectionsLookup.data ?? [];
  const selectedCollections = collectionOptions.filter((option) =>
    documentCollections.data?.some((collection) => collection.id === option.value)
  );
  const canEditCollections = state.actions.editCollections === DocumentActionState.Enabled;

  const changeCollections = (selected: MultiValue<Option>) => {
    updateDocumentCollections.mutate(selected.map((option) => option.value));
  };

  return (
    <>
      <WizardTextInputControlledComponent
        label="UUID of data set"
        tooltip="Automatically generated universally unique identifier of this data."
        name="uuid"
        value={docId}
        disabled
      />
      <WizardTextInputControlledComponent
        label="EPD Id"
        name="registrationNumber"
        tooltip="Automatically generated unique identifier for this EPD."
        value={metadata.fullIdentificationNumber}
        disabled
      />
      <div>
        <WizardTextInputControlledComponent
          label="EPD Version"
          tooltip="Automatically generated version number to track the EPD changes."
          name="versionNumber"
          value={metadata.versionNumber.toString().padStart(3, '0')}
          disabled
        />
        <SmartLabel label="Collection(s)" tooltip="The EPD collections of your company that this EPD belongs to." />
        <EpdSelectComponent
          options={collectionOptions}
          isMulti
          value={selectedCollections}
          onChange={changeCollections}
          isDisabled={
            !canEditCollections ||
            collectionsLookup.isLoading ||
            documentCollections.isLoading ||
            updateDocumentCollections.isPending
          }
        />
        <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'auto auto auto 1fr' }}>
          <VersionHistoryDialog docId={docId} versions={documentVersions.data ?? []} />
          {documentVersions.data && documentVersions.data.length > 1 && (isAdmin || isLicenseeAdmin) && (
            <VersionComparerDialog
              docId={docId}
              currentVersionId={metadata.documentVersionId}
              versions={documentVersions.data}
            />
          )}
          <DocumentCommentLogButton docId={docId} />
        </div>
      </div>
    </>
  );
};

export default EpdMetadataRenderer;
